import {
	Alert,
	AsyncSelect,
	AsyncSelectProps,
	Box,
	Inline,
} from "@sembark-travel/ui-base"
import { useDialog } from "@sembark-travel/ui-dialog"
import { useXHR } from "@sembark-travel/xhr"
import type { IHotel } from "../Hotels"
import {
	AXISROOMS,
	CHANNEL_MANAGERS,
	channelManagersXHR,
	DJUBO,
	getChannelManagerName,
	type TChannelManagerOption,
} from "./store"
import { DjuboSimulator, DjuboSyncAriDialog } from "./DjuboSimulator"
import {
	AxisRoomsSimulator,
	AxisRoomsSyncAriDialog,
} from "./AxisRoomsSimulator"

/**
 * Logo of a channel manager. Render nothing when the channel manager is
 * unknown or has no icon, so callers can drop it in without a guard.
 */
export function ChannelManagerIcon({
	channelManager,
	size = "24px",
}: {
	channelManager: string | null | undefined
	size?: string
}) {
	const config = channelManager ? CHANNEL_MANAGERS[channelManager] : undefined
	if (!config?.iconUrl) {
		return null
	}
	// a unitless size is not valid css, so normalise it
	const dimension = /^\d+$/.test(size) ? `${size}px` : size
	return (
		<Box
			as="img"
			src={config.iconUrl}
			alt={`${config.name} logo`}
			style={{
				// the size set the height and the width follow the logo's own
				// proportions: a square mark still come out square, while a
				// wordmark stay legible instead of being squeezed into a box
				// its letters can not be read in
				height: dimension,
				width: "auto",
				objectFit: "contain",
				// an img shrink inside a flex row, unlike an svg icon
				flexShrink: 0,
			}}
		/>
	)
}

// Custom option renderer that shows the channel manager icon before the name in dropdown list.
function ChannelManagerOptionRenderer({
	option,
}: {
	option: TChannelManagerOption
	labelKey: string
	created?: boolean
	createOptionLabel: (label: string) => React.ReactNode
}) {
	return (
		<Inline gap="2" alignItems="center">
			<ChannelManagerIcon channelManager={option.id} size="18px" />
			<Box as="span">{option.name}</Box>
		</Inline>
	)
}

type TChannelManagerSelectProps = Omit<AsyncSelectProps, "fetch">

export function ChannelManagerSelect({
	value,
	...props
}: TChannelManagerSelectProps) {
	const xhr = useXHR()
	const selectedOption = value as TChannelManagerOption | undefined
	return (
		<Inline gap="2" alignItems="center">
			<AsyncSelect
				{...props}
				value={value}
				fetch={() =>
					channelManagersXHR(xhr)
						.list()
						.then((items) =>
							items.map(
								(item): TChannelManagerOption => ({
									id: item.channel_manager,
									name: item.name,
								})
							)
						)
				}
				optionRenderer={ChannelManagerOptionRenderer}
			/>
			{selectedOption?.id ? (
				<Box display="flex" alignItems="center" flexShrink="0">
					<ChannelManagerIcon channelManager={selectedOption.id} size="22px" />
				</Box>
			) : null}
		</Inline>
	)
}

/**
 * Pick the Sync ARI dialog of the hotel's channel manager. Renders the trigger
 * via children and manages the dialog's open state internally. Renders no dialog
 * when the hotel has none, or when its channel manager has no dialog yet.
 */
export function ChannelManagerSyncAriDialog({
	hotel,
	children,
}: {
	hotel: IHotel
	children: (props: {
		open: () => void
		onClick: () => void
	}) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	const triggerProps = Object.assign(open, { open, onClick: open })

	function renderDialog() {
		switch (hotel.channel_manager) {
			case DJUBO:
				return (
					<DjuboSyncAriDialog hotel={hotel} open={isOpen} onClose={close} />
				)
			case AXISROOMS:
				return (
					<AxisRoomsSyncAriDialog hotel={hotel} open={isOpen} onClose={close} />
				)
			default:
				return null
		}
	}

	return (
		<>
			{children(triggerProps)}
			{renderDialog()}
		</>
	)
}

/**
 * Pick the simulator of the hotel's channel manager. Add a branch here, and an
 * entry in `CHANNEL_MANAGERS`, when another channel manager get a simulator.
 */
export function ChannelManagerSimulator({ hotel }: { hotel: IHotel }) {
	const channelManager = hotel.channel_manager

	if (!channelManager) {
		return (
			<Alert status="error" title="No channel manager">
				This hotel is not connected to a channel manager, so there is nothing to
				simulate. Connect one from the hotel's details to use this page.
			</Alert>
		)
	}

	switch (channelManager) {
		case DJUBO:
			return <DjuboSimulator hotel={hotel} />
		case AXISROOMS:
			return <AxisRoomsSimulator hotel={hotel} />
		default:
			return (
				<Alert status="error" title="Simulator not available">
					There is no simulator for the {getChannelManagerName(channelManager)}{" "}
					channel manager yet.
				</Alert>
			)
	}
}
